// ContainerPriceCalculation.jsx - COMPLETE FIXED VERSION
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalculator, faBox, faDollarSign, faWarehouse, faFileInvoice,
    faPlus, faTrash, faSave, faExclamationCircle, faTimes,
    faSpinner, faCheckCircle, faExclamationTriangle, faHistory,
    faClipboardList, faExchangeAlt, faStore, faEye, faEdit,
    faSearch, faUser, faPhone, faCalendarAlt, faTag,
    faChevronDown, faBoxes, faReceipt, faRuler
} from '@fortawesome/free-solid-svg-icons';

// Create translator instance
const createContainerPriceTranslator = () => {
    let cache = new Map();
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_container_price_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Container Price Calculation");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_container_price_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;
        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) { }
        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) return cache.get(cacheKey);
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        containerPriceCalculation: 'Container Price Calculation',
        fillDetails: 'Fill in the container shipment details below',
        containerLevelCharges: 'Container Level Charges',
        documentCharges: 'Document Charges (PKR)',
        usdAmount: 'USD Amount',
        dailyUsdRate: 'Daily USD Rate',
        storeRent: 'Store Rent (PKR)',
        supplierCommission: 'Supplier Commission',
        totalExpense: 'Total Expense',
        perKgExpense: 'Per KG Expense',
        totalContainerWeight: 'Total Container Weight (KG)',
        totalContainerYards: 'Total Container Yards',
        totalYards: 'Total Yards',
        itemsDetails: 'Items Details',
        itemName: 'Item Name',
        weightKg: 'Weight (KG)',
        dutyRate: 'Duty Rate',
        fabricRate: 'Fabric Rate (per meter USD)',
        dutyAmount: 'Duty Amount',
        totalKgExpense: 'Total KG Expense',
        perYardExpense: 'Per Yard Expense',
        perYardChina: 'Per Yard China',
        finalPKRPerYard: 'Final PKR / yd',
        finalUSDPerYard: 'Final USD / yd',
        addItem: 'Add Item',
        removeItem: 'Remove Item',
        saveCalculation: 'Save Calculation',
        updateCalculation: 'Update Calculation',
        clearForm: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        calculationSaved: 'Calculation saved successfully!',
        calculationUpdated: 'Calculation updated successfully!',
        calculationDeleted: 'Calculation deleted successfully!',
        failedSave: 'Failed to save calculation',
        failedLoad: 'Failed to load calculation',
        networkError: 'Network error occurred',
        noShopSelected: 'No shop selected. Please select a shop first.',
        selectCustomer: 'Select Customer',
        customerRequired: 'Customer name is required',
        itemNameRequired: 'Please enter item names for all items',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        searchCustomer: 'Search customer...',
        noCustomersFound: 'No customers found',
        calculationHistory: 'Calculation History',
        shipmentRef: 'Shipment Ref',
        customer: 'Customer',
        createdAt: 'Created Date',
        actions: 'Actions',
        viewDetails: 'View Details',
        edit: 'Edit',
        delete: 'Delete',
        confirmDelete: 'Are you sure you want to delete this calculation?',
        status: 'Status',
        draft: 'Draft',
        confirmed: 'Confirmed',
        shipped: 'Shipped',
        delivered: 'Delivered',
        currentShop: 'Current Shop',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        newCalculation: 'New Calculation',
        history: 'History',
        calculationDetails: 'Calculation Details',
        shipmentInfo: 'Shipment Information',
        generate: 'Generate',
        date: 'Date',
        grandTotal: 'Grand Total',
        paid: 'Paid Amount',
        balance: 'Balance',
        resetFilters: 'Reset',
        previous: 'Previous',
        next: 'Next',
        showing: 'Page',
        of: 'of',
        noRecords: 'No calculations found',
        singleItem: 'Single Item',
        mixItems: 'Mix Items',
        yardsPerItem: 'Yards',
        totalContainerYardsLabel: 'Total Container Yards'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') return defaultTexts;
        const translations = {};
        const promises = Object.keys(defaultTexts).map(async key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                const translated = await translateText(defaultTexts[key], lang);
                translations[key] = translated;
            }
        });
        await Promise.all(promises);
        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const containerPriceTranslator = createContainerPriceTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationCircle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Items Table Component - FIXED FORMULA FOR MIX ITEMS
const ItemsTable = ({ items, onItemChange, onRemove, onAdd, errors, disabled, translations, isRTL, perKgExpense, rate, mode, totalContainerYards }) => {
    const tableRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const displayItems = items || [];

    const formatNumber = (value, decimals = 2) => {
        if (value === undefined || value === null || isNaN(value) || !isFinite(value)) return "0.00";
        return Number(value).toFixed(decimals);
    };

    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4>
                    <FontAwesomeIcon icon={faClipboardList} className="header-icon-only" />
                    {translations?.itemsDetails || 'Items Details'}
                </h4>
                <div style={{ fontSize: '14px', color: '#666', marginLeft: 'auto', marginRight: '16px' }}>
                    <strong>{translations?.totalContainerYardsLabel || 'Total Yards'}:</strong> {formatNumber(totalContainerYards)}
                </div>
                {mode === 'mix' && (
                    <button
                        type="button"
                        className="btn-add-item-table"
                        onClick={onAdd}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                )}
            </div>
            <div className="table-wrapper" ref={tableRef} style={{ overflowX: 'auto' }}>
                <table className="items-table" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1300px' }}>
                    <thead>
                        <tr style={{ background: '#f8fafc' }}>
                            <th style={{ width: '8%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.itemName || 'Item Name'} <span className="required">*</span></th>
                            <th style={{ width: '8%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.weightKg || 'Weight (KG)'}</th>
                            <th style={{ width: '8%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.dutyRate || 'Duty Rate'}</th>
                            <th style={{ width: '8%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.yardsPerItem || 'Yards'}</th>
                            <th style={{ width: '8%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.dutyAmount || 'Duty Amount'}</th>
                            <th style={{ width: '10%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.totalKgExpense || 'Total KG Expense'}</th>
                            <th style={{ width: '10%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.perYardExpense || 'Per Yard Expense'}</th>
                            <th style={{ width: '10%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.fabricRate || 'Fabric Rate (USD/m)'}</th>
                            <th style={{ width: '10%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.finalPKRPerYard || 'Final PKR / yd'}</th>
                            <th style={{ width: '10%', padding: '12px 8px', textAlign: 'left', fontSize: '13px' }}>{translations?.finalUSDPerYard || 'Final USD / yd'}</th>
                            {mode === 'mix' && (
                                <th style={{ width: '5%', padding: '12px 8px', textAlign: 'center', fontSize: '13px' }}></th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {displayItems.map((item, index) => {
                            const kg = parseFloat(item.kg) || 0;
                            const dutyRateNum = parseFloat(item.dutyRate) || 0;
                            const perKgExpenseNum = parseFloat(perKgExpense) || 0;
                            const rateNum = parseFloat(rate) || 0;
                            
                            // EACH ITEM HAS ITS OWN YARDS
                            const itemYards = parseFloat(item.totalYards) || 0;
                            
                            // Calculate Duty Amount = Weight × Duty Rate
                            const dutyAmount = kg * dutyRateNum;
                            
                            // FIXED FORMULA: Total KG Expense = Weight × Per KG Expense
                            const totalKgExpenseVal = kg * perKgExpenseNum;
                            
                            // FIXED FORMULA: Per Yard Expense = (Weight × Per KG Expense) / Yards
                            // This is the key fix - weight * per kg expense = result / yards
                            const perYardExpenseVal = itemYards > 0 
                                ? totalKgExpenseVal / itemYards 
                                : 0;
                            
                            const fabricRate = parseFloat(item.fabrics?.[0]?.perMeterRate) || 0;
                            
                            // Per Yard China = Fabric Rate × 0.9144 × Exchange Rate
                            const perYardChina = fabricRate * 0.9144 * rateNum;
                            
                            // Final PKR = Per Yard China + Per Yard Expense
                            const finalPKR = perYardChina + perYardExpenseVal;
                            
                            // Final USD = Final PKR / Exchange Rate
                            const finalUSD = rateNum > 0 ? finalPKR / rateNum : 0;
                            
                            return (
                                <tr key={item.id || index} className={errors[`name_${index}`] ? 'error-row' : ''} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="text"
                                            value={item.name || ''}
                                            onChange={(e) => onItemChange(index, 'name', e.target.value)}
                                            placeholder={translations?.itemName || 'Item name'}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                            className={errors[`name_${index}`] ? 'error' : ''}
                                            style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                        {errors[`name_${index}`] && (
                                            <div style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>
                                                <FontAwesomeIcon icon={faExclamationTriangle} /> {errors[`name_${index}`]}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={item.kg || ''}
                                            onChange={(e) => onItemChange(index, 'kg', e.target.value)}
                                            placeholder="0.00"
                                            disabled={disabled}
                                            style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={item.dutyRate || ''}
                                            onChange={(e) => onItemChange(index, 'dutyRate', e.target.value)}
                                            placeholder="0.00"
                                            disabled={disabled}
                                            style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="number"
                                            step="0.01"
                                            value={item.totalYards || ''}
                                            onChange={(e) => onItemChange(index, 'totalYards', e.target.value)}
                                            placeholder="0.00"
                                            disabled={disabled}
                                            style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="text"
                                            value={formatNumber(dutyAmount)}
                                            readOnly
                                            style={{ width: '100%', padding: '8px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="text"
                                            value={formatNumber(totalKgExpenseVal)}
                                            readOnly
                                            style={{ width: '100%', padding: '8px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="text"
                                            value={formatNumber(perYardExpenseVal, 4)}
                                            readOnly
                                            style={{ width: '100%', padding: '8px', background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '6px', fontWeight: 'bold', color: '#2e7d32', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="number"
                                            step="0.0001"
                                            value={fabricRate || ''}
                                            onChange={(e) => onItemChange(index, 'fabricRate', e.target.value)}
                                            placeholder="0.0000"
                                            disabled={disabled}
                                            style={{ width: '100%', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '6px', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="text"
                                            value={formatNumber(finalPKR, 2)}
                                            readOnly
                                            style={{ width: '100%', padding: '8px', background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: '6px', fontWeight: 'bold', color: '#2e7d32', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </td>
                                    <td style={{ padding: '8px' }}>
                                        <input
                                            type="text"
                                            value={formatNumber(finalUSD, 4)}
                                            readOnly
                                            style={{ width: '100%', padding: '8px', background: '#e3f2fd', border: '1px solid #bbdefb', borderRadius: '6px', fontWeight: 'bold', color: '#1565c0', boxSizing: 'border-box', fontSize: '13px' }}
                                        />
                                    </td>
                                    {mode === 'mix' && (
                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                            <button
                                                type="button"
                                                onClick={() => onRemove(index)}
                                                disabled={disabled || displayItems.length === 1}
                                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '16px' }}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// Main Component
const ContainerPriceCalculation = ({ calculationId = null, onSuccess = null }) => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (currentShop?.shop_id) return currentShop.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    const [mode, setMode] = useState('single');

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        bill_no: '',
        customer_id: '',
        customer_name: '',
        document_charges: '',
        usd_amount: '',
        daily_usd_rate: '',
        store_rent: '',
        grand_total: '0.00',
        paid: '0.00',
        balance: '0.00',
        items: [{ id: Date.now(), name: '', kg: '', dutyRate: '', totalYards: '', fabrics: [{ perMeterRate: '' }] }],
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyPage, setHistoryPage] = useState(1);
    const [selectedShipment, setSelectedShipment] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editShipmentId, setEditShipmentId] = useState(null);
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);
    const [summary, setSummary] = useState(null);
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);

    const [historyFilters, setHistoryFilters] = useState({
        customer_name: '',
        shipment_ref: '',
        status: '',
        start_date: '',
        end_date: ''
    });

    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        setToast({ show: true, type, message, direction: isRTL ? 'rtl' : 'ltr', progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            setToast(prev => ({ ...prev, progress: (timeLeft / 3) * 100, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) { clearInterval(timer); setToast(prev => ({ ...prev, show: false })); }
        }, 100);
        toastTimerRef.current = timer;
    }, [currentLanguage]);

    useEffect(() => {
        return () => { if (toastTimerRef.current) clearInterval(toastTimerRef.current); };
    }, []);

    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;
        setIsTranslating(true);
        try {
            const trans = await containerPriceTranslator.getTranslations(lang);
            setTranslations(trans);
            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';
        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);
        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };
        window.addEventListener('storage', handleLanguageChange);
        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);
        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        containerPriceTranslator.addListener(translationListener);
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            containerPriceTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };
                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };
                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });
            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };
        applyTheme(formData.themeColor);
        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };
        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);
        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    // Total Container Yards - Sum of all item yards
    const totalContainerYards = useMemo(() => {
        return formData.items.reduce((sum, item) => sum + (parseFloat(item.totalYards) || 0), 0);
    }, [formData.items]);

    // Total Container Weight - Sum of all item weights
    const totalContainerWeight = useMemo(() => {
        return formData.items.reduce((sum, item) => sum + (parseFloat(item.kg) || 0), 0);
    }, [formData.items]);

    // Container Level Calculations
    const containerCalculation = useMemo(() => {
        const doc = parseFloat(formData.document_charges) || 0;
        const usd = parseFloat(formData.usd_amount) || 0;
        const rate = parseFloat(formData.daily_usd_rate) || 0;
        const rent = parseFloat(formData.store_rent) || 0;
        
        const supplierCommission = usd * rate;
        const totalExpense = doc + supplierCommission + rent;
        const perKgExpense = totalContainerWeight > 0 ? totalExpense / totalContainerWeight : 0;
        
        return { supplierCommission, totalExpense, perKgExpense, rate };
    }, [formData.document_charges, formData.usd_amount, formData.daily_usd_rate, formData.store_rent, totalContainerWeight]);

    useEffect(() => {
        const grandTotal = containerCalculation.totalExpense;
        const paid = parseFloat(formData.paid) || 0;
        const balance = grandTotal - paid;
        setFormData(prev => ({
            ...prev,
            grand_total: grandTotal.toFixed(2),
            balance: balance.toFixed(2)
        }));
    }, [containerCalculation.totalExpense, formData.paid]);

    const handleModeToggle = () => {
        const newMode = mode === 'single' ? 'mix' : 'single';
        setMode(newMode);
        
        if (newMode === 'single') {
            setFormData(prev => ({
                ...prev,
                items: prev.items.slice(0, 1)
            }));
        } else {
            if (formData.items.length === 1) {
                setFormData(prev => ({
                    ...prev,
                    items: [
                        ...prev.items,
                        { id: Date.now() + 1, name: '', kg: '', dutyRate: '', totalYards: '', fabrics: [{ perMeterRate: '' }] }
                    ]
                }));
            }
        }
        setErrors({});
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        if (field === 'fabricRate') {
            if (!updatedItems[index].fabrics) updatedItems[index].fabrics = [{ perMeterRate: '' }];
            updatedItems[index].fabrics[0].perMeterRate = value;
        } else {
            updatedItems[index][field] = value;
        }
        setFormData(prev => ({ ...prev, items: updatedItems }));
        
        const errorKey = `${field}_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[errorKey]; return newErrors; });
        }
    };

    const addItem = () => {
        if (mode === 'single') {
            showToast('warning', 'Cannot add multiple items in Single Item mode');
            return;
        }
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { id: Date.now(), name: '', kg: '', dutyRate: '', totalYards: '', fabrics: [{ perMeterRate: '' }] }]
        }));
    };

    const removeItem = (index) => {
        if (mode === 'single') {
            showToast('warning', 'Cannot remove item in Single Item mode');
            return;
        }
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({ ...prev, items: updatedItems }));
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.customer_name?.trim()) {
            newErrors.customer = translations?.customerRequired || 'Customer name is required';
        }
        formData.items.forEach((item, index) => {
            if (!item.name?.trim()) {
                newErrors[`name_${index}`] = translations?.itemNameRequired || 'Item name is required';
            }
            if (!item.kg || parseFloat(item.kg) <= 0) {
                newErrors[`kg_${index}`] = 'Valid weight is required';
            }
            if (!item.totalYards || parseFloat(item.totalYards) <= 0) {
                newErrors[`totalYards_${index}`] = 'Valid yards is required';
            }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const loadHistory = useCallback(async () => {
        if (!currentShop?.shop_id && !shopId) return;
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/container_price_calculation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_calculations',
                    username: user?.username,
                    shop_id: currentShop?.shop_id || shopId,
                    page: historyPage,
                    limit: 20,
                    ...historyFilters
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setHistoryData(data.data || []);
                setHistoryTotal(data.total || 0);
            }
        } catch (error) {
            console.error('Error loading history:', error);
            showToast('error', translations?.failedLoad || 'Failed to load history');
        } finally {
            setLoading(false);
        }
    }, [currentShop, shopId, user, historyPage, historyFilters, translations, showToast]);

    const loadSummary = useCallback(async () => {
        if (!currentShop?.shop_id && !shopId) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/container_price_calculation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_summary',
                    shop_id: currentShop?.shop_id || shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') setSummary(data.data);
        } catch (error) {
            console.error('Error loading summary:', error);
        }
    }, [currentShop, shopId]);

    const generateNextBillNumber = () => {
        const date = new Date();
        const year = date.getFullYear().toString().substr(-2);
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
        return `CONT-${year}${month}${day}-${random}`;
    };

    const handleGenerateBillNumber = () => {
        if (!formData.bill_no) {
            setFormData(prev => ({
                ...prev,
                bill_no: generateNextBillNumber()
            }));
        }
    };

    // FIXED: Handle form submission with per-item yards calculation
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        if (!currentShop && !shopId) {
            showToast('error', translations?.noShopSelected || 'Please select a shop first');
            setShowShopSelector(true);
            return;
        }

        setSaving(true);

        const rate = parseFloat(formData.daily_usd_rate) || 0;
        const perKgExpense = containerCalculation.perKgExpense;

        const processedItems = formData.items.map((item) => {
            const kg = parseFloat(item.kg) || 0;
            const dutyRate = parseFloat(item.dutyRate) || 0;
            const totalYards = parseFloat(item.totalYards) || 0;
            
            const dutyAmount = kg * dutyRate;
            // FIXED FORMULA: Total KG Expense = Per KG Expense × Weight
            const totalKgExpense = perKgExpense * kg;
            // FIXED FORMULA: Per Yard Expense = Total KG Expense / Yards
            const perYardExpense = totalYards > 0 ? totalKgExpense / totalYards : 0;
            const fabricRate = parseFloat(item.fabrics?.[0]?.perMeterRate) || 0;
            const perYardChina = fabricRate * 0.9144 * rate;
            const finalPKR = perYardChina + perYardExpense;
            const finalUSD = rate > 0 ? finalPKR / rate : 0;

            return {
                name: item.name,
                kg: kg,
                duty_rate: dutyRate,
                total_yards: totalYards,
                duty: dutyAmount,
                total_kg_expense: totalKgExpense,
                per_yard_expense: perYardExpense,
                fabrics: [
                    {
                        per_meter_rate: fabricRate,
                        per_yard_china: perYardChina,
                        final_pkr: finalPKR,
                        final_usd: finalUSD,
                        fabric_index: 0
                    }
                ]
            };
        });

        const payload = {
            action: isEditMode ? 'update_calculation' : 'save_calculation',
            username: user?.username,
            shop_id: currentShop?.shop_id || shopId,
            shipment_ref: formData.bill_no,
            date: formData.date,
            customer_id: formData.customer_id,
            customer_name: formData.customer_name,
            document_charges: parseFloat(formData.document_charges) || 0,
            usd_amount: parseFloat(formData.usd_amount) || 0,
            daily_usd_rate: rate,
            store_rent: parseFloat(formData.store_rent) || 0,
            supplier_commission: containerCalculation.supplierCommission,
            total_expense: containerCalculation.totalExpense,
            per_kg_expense: containerCalculation.perKgExpense,
            total_container_weight: totalContainerWeight,
            total_container_yards: totalContainerYards,
            mode: mode,
            grand_total: containerCalculation.totalExpense,
            paid: parseFloat(formData.paid) || 0,
            balance: (containerCalculation.totalExpense - (parseFloat(formData.paid) || 0)),
            items: processedItems
        };

        if (isEditMode && editShipmentId) payload.shipment_id = editShipmentId;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/container_price_calculation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', isEditMode ? (translations?.calculationUpdated || 'Calculation updated successfully!') : (translations?.calculationSaved || 'Calculation saved successfully!'));
                resetForm();
                loadHistory();
                loadSummary();
                if (onSuccess) onSuccess(data.shipment_id);
            } else {
                showToast('error', data.message || translations?.failedSave || 'Failed to save calculation');
            }
        } catch (error) {
            console.error('Error saving calculation:', error);
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setFormData({
            date: new Date().toISOString().split('T')[0],
            bill_no: '',
            customer_id: '',
            customer_name: '',
            document_charges: '',
            usd_amount: '',
            daily_usd_rate: '',
            store_rent: '',
            grand_total: '0.00',
            paid: '0.00',
            balance: '0.00',
            items: [{ id: Date.now(), name: '', kg: '', dutyRate: '', totalYards: '', fabrics: [{ perMeterRate: '' }] }],
            themeColor: formData.themeColor
        });
        setErrors({});
        setIsEditMode(false);
        setEditShipmentId(null);
    };

    const viewShipmentDetails = async (shipmentId) => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/container_price_calculation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_calculation_details',
                    shipment_id: shipmentId,
                    shop_id: currentShop?.shop_id || shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                setSelectedShipment(data.data);
                setShowViewModal(true);
            } else {
                showToast('error', data.message || 'Failed to load shipment details');
            }
        } catch (error) {
            console.error('Error loading shipment details:', error);
            showToast('error', translations?.networkError || 'Failed to load shipment details');
        } finally {
            setLoading(false);
        }
    };

    const editShipment = async (shipmentId) => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/container_price_calculation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_calculation_details',
                    shipment_id: shipmentId,
                    shop_id: currentShop?.shop_id || shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                const shipment = data.data.shipment;
                const itemsData = data.data.items;

                const modeFromData = itemsData.length > 1 ? 'mix' : 'single';
                setMode(modeFromData);

                setFormData(prev => ({
                    ...prev,
                    date: shipment.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
                    bill_no: shipment.shipment_ref || '',
                    customer_id: shipment.customer_id || '',
                    customer_name: shipment.customer_name || '',
                    document_charges: shipment.document_charges || '',
                    usd_amount: shipment.usd_amount || '',
                    daily_usd_rate: shipment.daily_usd_rate || '',
                    store_rent: shipment.store_rent || '',
                    paid: '0.00'
                }));

                const formattedItems = itemsData.map(item => ({
                    id: item.id,
                    name: item.item_name || '',
                    kg: item.kg || '',
                    dutyRate: item.duty_rate || '',
                    totalYards: item.total_yards || '',
                    fabrics: item.fabrics && item.fabrics.length > 0
                        ? [{ perMeterRate: item.fabrics[0]?.per_meter_rate || '' }]
                        : [{ perMeterRate: '' }]
                }));
                setFormData(prev => ({ ...prev, items: formattedItems }));
                setIsEditMode(true);
                setEditShipmentId(shipmentId);
                setShowHistory(false);
                showToast('success', 'Loaded calculation for editing');
            } else {
                showToast('error', data.message || 'Failed to load calculation');
            }
        } catch (error) {
            console.error('Error loading calculation for edit:', error);
            showToast('error', translations?.networkError || 'Failed to load calculation');
        } finally {
            setLoading(false);
        }
    };

    const deleteShipment = async (shipmentId) => {
        if (!window.confirm(translations?.confirmDelete || 'Are you sure you want to delete this calculation?')) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/container_price_calculation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_calculation',
                    shipment_id: shipmentId,
                    username: user?.username,
                    shop_id: currentShop?.shop_id || shopId
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations?.calculationDeleted || 'Calculation deleted successfully');
                loadHistory();
                loadSummary();
            } else {
                showToast('error', data.message || 'Failed to delete');
            }
        } catch (error) {
            console.error('Error deleting:', error);
            showToast('error', translations?.networkError || 'Network error occurred');
        }
    };

    const fetchAvailableShops = async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
        } finally {
            setLoadingShops(false);
        }
    };

    const handleSwitchShop = async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                setShopId(shop.shop_id);
                showToast('success', 'Shop switched successfully');
                setShowShopSelector(false);
                resetForm();
                loadHistory();
                loadSummary();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    };

    useEffect(() => {
        if (user && (currentShop || shopId)) {
            loadHistory();
            loadSummary();
        }
        fetchAvailableShops();
    }, [user, currentShop, shopId]);

    useEffect(() => {
        if (showHistory) { setHistoryPage(1); loadHistory(); }
    }, [historyFilters, showHistory]);

    useEffect(() => {
        if (showHistory) loadHistory();
    }, [historyPage]);

    const getStatusBadgeStyle = (status) => {
        const styles = {
            'draft': { backgroundColor: '#9ca3af', color: '#1f2937' },
            'confirmed': { backgroundColor: '#3b82f6', color: '#ffffff' },
            'shipped': { backgroundColor: '#f59e0b', color: '#ffffff' },
            'delivered': { backgroundColor: '#10b981', color: '#ffffff' }
        };
        return styles[status] || styles['draft'];
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
    const totalPages = Math.ceil(historyTotal / 20);
    const grandTotal = containerCalculation.totalExpense;

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faCalculator} />
                    </div>
                    <div className="header-text">
                        <h1>{translations?.containerPriceCalculation || 'Container Price Calculation'}</h1>
                        <p>{translations?.fillDetails || 'Fill in the container shipment details below'}</p>
                    </div>
                </div>
                
                <div className="mode-toggle-container" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: mode === 'single' ? 'var(--purchase-primary-color)' : '#64748b' }}>
                        {translations?.singleItem || 'Single Item'}
                    </span>
                    <button
                        type="button"
                        className="mode-toggle-btn"
                        onClick={handleModeToggle}
                        style={{
                            position: 'relative',
                            width: '56px',
                            height: '30px',
                            borderRadius: '15px',
                            border: 'none',
                            cursor: 'pointer',
                            background: mode === 'mix' ? 'var(--purchase-primary-color)' : '#cbd5e1',
                            transition: 'background 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 3px'
                        }}
                    >
                        <div
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '50%',
                                background: 'white',
                                transform: mode === 'mix' ? 'translateX(26px)' : 'translateX(0)',
                                transition: 'transform 0.3s ease',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }}
                        />
                    </button>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: mode === 'mix' ? 'var(--purchase-primary-color)' : '#64748b' }}>
                        {translations?.mixItems || 'Mix Items'}
                    </span>
                </div>
                
                {isTranslating && (
                    <div className="translation-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Translating...</span>
                    </div>
                )}
            </div>

            <form onSubmit={handleSubmit} className="modern-form">
                <div className="form-section">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faReceipt} />
                        {translations?.shipmentInfo || 'Shipment Information'}
                    </h3>

                    <div className="form-grid-modern">
                        <div className={`form-group-modern ${errors.date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="date" className="input-label">
                                    {translations?.date || 'Date'}
                                </label>
                            </div>
                            {errors.date && (
                                <div className="error-message-modern">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    {errors.date}
                                </div>
                            )}
                        </div>

                        <div className={`form-group-modern ${errors.bill_no ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                <input
                                    type="text"
                                    id="bill_no"
                                    name="bill_no"
                                    value={formData.bill_no}
                                    onChange={handleChange}
                                    placeholder={translations?.shipmentRef || 'Shipment reference'}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="bill_no" className="input-label">
                                    {translations?.shipmentRef || 'Shipment Ref'}
                                </label>
                                <button
                                    type="button"
                                    className="generate-btn"
                                    onClick={handleGenerateBillNumber}
                                    disabled={saving || !!formData.bill_no}
                                    title={translations?.generate || 'Generate'}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                            </div>
                            {errors.bill_no && (
                                <div className="error-message-modern">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    {errors.bill_no}
                                </div>
                            )}
                        </div>

                        <div className={`form-group-modern ${errors.customer ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input
                                    type="text"
                                    id="customer_name"
                                    name="customer_name"
                                    value={formData.customer_name}
                                    onChange={handleChange}
                                    placeholder={translations?.customer || 'Customer name'}
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                />
                                <label htmlFor="customer_name" className="input-label">
                                    {translations?.customer || 'Customer'} <span className="required">*</span>
                                </label>
                            </div>
                            {errors.customer && (
                                <div className="error-message-modern">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    {errors.customer}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <h3 className="section-title">
                        <FontAwesomeIcon icon={faBox} />
                        {translations?.containerLevelCharges || 'Container Level Charges'}
                    </h3>

                    <div className="form-grid-modern">
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                <input
                                    type="number"
                                    step="0.01"
                                    id="document_charges"
                                    name="document_charges"
                                    value={formData.document_charges}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="document_charges" className="input-label">
                                    {translations?.documentCharges || 'Document Charges (PKR)'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faDollarSign} className="input-icon" />
                                <input
                                    type="number"
                                    step="0.01"
                                    id="usd_amount"
                                    name="usd_amount"
                                    value={formData.usd_amount}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="usd_amount" className="input-label">
                                    {translations?.usdAmount || 'USD Amount'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faExchangeAlt} className="input-icon" />
                                <input
                                    type="number"
                                    step="0.0001"
                                    id="daily_usd_rate"
                                    name="daily_usd_rate"
                                    value={formData.daily_usd_rate}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="daily_usd_rate" className="input-label">
                                    {translations?.dailyUsdRate || 'Daily USD Rate'}
                                </label>
                            </div>
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faWarehouse} className="input-icon" />
                                <input
                                    type="number"
                                    step="0.01"
                                    id="store_rent"
                                    name="store_rent"
                                    value={formData.store_rent}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="store_rent" className="input-label">
                                    {translations?.storeRent || 'Store Rent (PKR)'}
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="calculated-values-modern" style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--purchase-primary-ultralight)', borderRadius: '12px' }}>
                        <div className="calc-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <span className="calc-label">{translations?.supplierCommission || 'Supplier Commission'} (USD × Rate):</span>
                            <span className="calc-value">PKR {containerCalculation.supplierCommission.toFixed(2)}</span>
                        </div>
                        <div className="calc-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <span className="calc-label">{translations?.totalExpense || 'Total Expense'}:</span>
                            <span className="calc-value">PKR {containerCalculation.totalExpense.toFixed(2)}</span>
                        </div>
                        <div className="calc-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <span className="calc-label">{translations?.perKgExpense || 'Per KG Expense'}:</span>
                            <span className="calc-value">PKR {containerCalculation.perKgExpense.toFixed(4)}</span>
                        </div>
                        <div className="calc-item" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                            <span className="calc-label">{translations?.totalContainerWeight || 'Total Container Weight'}:</span>
                            <span className="calc-value">{totalContainerWeight.toFixed(2)} KG</span>
                        </div>
                        <div className="calc-item" style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span className="calc-label">{translations?.totalContainerYardsLabel || 'Total Container Yards'}:</span>
                            <span className="calc-value">{totalContainerYards.toFixed(2)} Yards</span>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <ItemsTable
                        items={formData.items}
                        onItemChange={handleItemChange}
                        onRemove={removeItem}
                        onAdd={addItem}
                        errors={errors}
                        disabled={saving}
                        translations={translations}
                        isRTL={isRTL}
                        perKgExpense={containerCalculation.perKgExpense}
                        rate={containerCalculation.rate}
                        mode={mode}
                        totalContainerYards={totalContainerYards}
                    />
                </div>

                <div className="form-section">
                    <div className="calculations-grid">
                        <div className="calculation-row grand-total">
                            <span className="calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                            <span className="calculation-value">PKR {grandTotal.toFixed(2)}</span>
                        </div>

                        <div className={`calculation-row paid-field ${errors.paid ? 'has-error' : ''}`}>
                            <span className="calculation-label">{translations?.paid || 'Paid Amount'}:</span>
                            <div className="paid-input-wrapper">
                                <input
                                    type="number"
                                    id="paid"
                                    name="paid"
                                    value={formData.paid}
                                    onChange={handleChange}
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    disabled={saving}
                                    dir="ltr"
                                    className={errors.paid ? 'error' : ''}
                                />
                            </div>
                        </div>

                        <div className="calculation-row balance">
                            <span className="calculation-label">{translations?.balance || 'Balance'}:</span>
                            <span className={`calculation-value ${parseFloat(formData.balance) > 0 ? 'negative' : 'positive'}`}>
                                PKR {formData.balance}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={resetForm}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations?.clearForm || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={saving || isTranslating}
                    >
                        {saving ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{isEditMode ? (translations?.updateCalculation || 'Update') : (translations?.saveCalculation || 'Save')}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>
            </form>

            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations?.loading || 'Loading...'}</p>
                        <p className="loading-subtext">Please wait while we load data</p>
                    </div>
                </div>
            )}

            {showShopSelector && (
                <div className="modal-overlay" onClick={() => setShowShopSelector(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{translations?.switchShop || 'Switch Shop'}</h3>
                            <button className="modal-close" onClick={() => setShowShopSelector(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body">
                            {loadingShops ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                                    <p>{translations?.loadingShops || 'Loading shops...'}</p>
                                </div>
                            ) : availableShops.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem' }}>
                                    <p>{translations?.noShops || 'No shops available'}</p>
                                </div>
                            ) : (
                                <div className="shop-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    {availableShops.map((shop) => (
                                        <div
                                            key={shop.shop_id}
                                            className={`shop-item ${currentShop?.shop_id === shop.shop_id ? 'active' : ''}`}
                                            onClick={() => handleSwitchShop(shop)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '1rem',
                                                padding: '1rem',
                                                borderRadius: '12px',
                                                cursor: 'pointer',
                                                background: currentShop?.shop_id === shop.shop_id ? 'var(--purchase-primary-ultralight)' : '#f9fafb',
                                                border: currentShop?.shop_id === shop.shop_id ? '1px solid var(--purchase-primary-color)' : '1px solid #e5e7eb'
                                            }}
                                        >
                                            <div className="shop-item-icon">
                                                <FontAwesomeIcon icon={faStore} size="lg" />
                                            </div>
                                            <div className="shop-item-details" style={{ flex: 1 }}>
                                                <div className="shop-item-name" style={{ fontWeight: 600 }}>{shop.shop_name}</div>
                                                <div className="shop-item-code" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                                    {shop.shop_code || `ID: ${shop.shop_id}`}
                                                </div>
                                            </div>
                                            {currentShop?.shop_id === shop.shop_id && (
                                                <div className="shop-item-current" style={{ color: 'var(--purchase-primary-color)' }}>
                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                    <span style={{ marginLeft: '0.5rem' }}>{translations?.currentShop || 'Current'}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showViewModal && selectedShipment && (
                <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
                    <div className="modal-content view-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{translations?.calculationDetails || 'Calculation Details'} - {selectedShipment.shipment?.shipment_ref}</h3>
                            <button className="modal-close" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="shipment-info-section">
                                <h4>{translations?.shipmentInfo || 'Shipment Information'}</h4>
                                <div className="info-grid">
                                    <div className="info-item"><strong>{translations?.customer || 'Customer'}:</strong> {selectedShipment.shipment?.customer_name}</div>
                                    <div className="info-item"><strong>{translations?.totalExpense || 'Total Expense'}:</strong> PKR {parseFloat(selectedShipment.shipment?.total_expense).toFixed(2)}</div>
                                    <div className="info-item"><strong>{translations?.status || 'Status'}:</strong> {selectedShipment.shipment?.status}</div>
                                    <div className="info-item"><strong>{translations?.createdAt || 'Created'}:</strong> {new Date(selectedShipment.shipment?.created_at).toLocaleString()}</div>
                                </div>
                            </div>
                            <div className="items-info-section">
                                <h4>{translations?.itemsDetails || 'Items Details'}</h4>
                                {selectedShipment.items?.map((item, idx) => (
                                    <div key={item.id} className="view-item-card">
                                        <div className="view-item-header"><strong>{item.item_name}</strong></div>
                                        <div className="view-item-details">
                                            <span>Weight: {item.kg} KG</span>
                                            <span>Duty Rate: {item.duty_rate}</span>
                                            <span>Total Yards: {item.total_yards}</span>
                                        </div>
                                        {item.fabrics && item.fabrics.map((fabric, fidx) => (
                                            <div key={fidx} className="view-fabric-details">
                                                <strong>Fabric:</strong>
                                                <span>Per Yard China: PKR {fabric.per_yard_china}</span>
                                                <span>Final PKR / yd: PKR {fabric.final_pkr}</span>
                                                <span>Final USD / yd: ${fabric.final_usd}</span>
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div style={{ marginTop: '2rem' }}>
                <div className="toggle-buttons-modern" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                    <button
                        onClick={() => setShowHistory(false)}
                        className={`toggle-btn ${!showHistory ? 'active' : ''}`}
                        style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: '12px',
                            border: 'none',
                            cursor: 'pointer',
                            background: !showHistory ? 'var(--purchase-primary-color)' : '#f3f4f6',
                            color: !showHistory ? 'white' : '#374151'
                        }}
                    >
                        <FontAwesomeIcon icon={faCalculator} /> {translations?.newCalculation || 'New Calculation'}
                    </button>
                    <button
                        onClick={() => { setShowHistory(true); loadHistory(); }}
                        className={`toggle-btn ${showHistory ? 'active' : ''}`}
                        style={{
                            padding: '0.75rem 1.5rem',
                            borderRadius: '12px',
                            border: 'none',
                            cursor: 'pointer',
                            background: showHistory ? 'var(--purchase-primary-color)' : '#f3f4f6',
                            color: showHistory ? 'white' : '#374151'
                        }}
                    >
                        <FontAwesomeIcon icon={faHistory} /> {translations?.history || 'History'}
                    </button>
                </div>

                {showHistory && (
                    <div className="history-section-modern">
                        <div className="filters-section-modern" style={{ marginBottom: '1.5rem' }}>
                            <div className="filters-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                                <div className="filter-group">
                                    <input
                                        type="text"
                                        placeholder={translations?.customer || 'Customer'}
                                        value={historyFilters.customer_name}
                                        onChange={(e) => setHistoryFilters(prev => ({ ...prev, customer_name: e.target.value }))}
                                        className="filter-input"
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                    />
                                </div>
                                <div className="filter-group">
                                    <input
                                        type="text"
                                        placeholder={translations?.shipmentRef || 'Shipment Ref'}
                                        value={historyFilters.shipment_ref}
                                        onChange={(e) => setHistoryFilters(prev => ({ ...prev, shipment_ref: e.target.value }))}
                                        className="filter-input"
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                    />
                                </div>
                                <div className="filter-group">
                                    <select
                                        value={historyFilters.status}
                                        onChange={(e) => setHistoryFilters(prev => ({ ...prev, status: e.target.value }))}
                                        className="filter-select"
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                    >
                                        <option value="">{translations?.status || 'All Status'}</option>
                                        <option value="draft">{translations?.draft || 'Draft'}</option>
                                        <option value="confirmed">{translations?.confirmed || 'Confirmed'}</option>
                                        <option value="shipped">{translations?.shipped || 'Shipped'}</option>
                                        <option value="delivered">{translations?.delivered || 'Delivered'}</option>
                                    </select>
                                </div>
                                <div className="filter-group">
                                    <input
                                        type="date"
                                        value={historyFilters.start_date}
                                        onChange={(e) => setHistoryFilters(prev => ({ ...prev, start_date: e.target.value }))}
                                        className="filter-input"
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                    />
                                </div>
                                <div className="filter-group">
                                    <input
                                        type="date"
                                        value={historyFilters.end_date}
                                        onChange={(e) => setHistoryFilters(prev => ({ ...prev, end_date: e.target.value }))}
                                        className="filter-input"
                                        style={{ width: '100%', padding: '0.75rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                    />
                                </div>
                                <button
                                    onClick={() => setHistoryFilters({ customer_name: '', shipment_ref: '', status: '', start_date: '', end_date: '' })}
                                    className="btn-reset-filters"
                                    style={{ padding: '0.75rem 1rem', borderRadius: '8px', background: '#f3f4f6', border: '1px solid #e5e7eb', cursor: 'pointer' }}
                                >
                                    {translations?.resetFilters || 'Reset'}
                                </button>
                            </div>
                        </div>

                        {loading ? (
                            <div className="loading-container" style={{ textAlign: 'center', padding: '2rem' }}>
                                <FontAwesomeIcon icon={faSpinner} spin /> {translations?.loading || 'Loading...'}
                            </div>
                        ) : historyData.length === 0 ? (
                            <div className="empty-state" style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                                {translations?.noRecords || 'No calculations found'}
                            </div>
                        ) : (
                            <>
                                <div className="table-container-modern">
                                    <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ background: '#f8fafc' }}>
                                                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '13px' }}>{translations?.shipmentRef || 'Shipment Ref'}</th>
                                                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '13px' }}>{translations?.customer || 'Customer'}</th>
                                                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '13px' }}>{translations?.totalExpense || 'Total Expense'}</th>
                                                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '13px' }}>{translations?.status || 'Status'}</th>
                                                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '13px' }}>{translations?.createdAt || 'Created Date'}</th>
                                                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '13px' }}>{translations?.actions || 'Actions'}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {historyData.map(record => (
                                                <tr key={record.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                                                    <td style={{ padding: '1rem', fontSize: '13px' }}>{record.shipment_ref}</td>
                                                    <td style={{ padding: '1rem', fontSize: '13px' }}>{record.customer_name}</td>
                                                    <td style={{ padding: '1rem', fontSize: '13px' }}>PKR {parseFloat(record.total_expense).toFixed(2)}</td>
                                                    <td style={{ padding: '1rem', fontSize: '13px' }}>
                                                        <span className="status-badge" style={{ ...getStatusBadgeStyle(record.status), padding: '0.25rem 0.75rem', borderRadius: '20px', fontSize: '0.875rem' }}>
                                                            {translations?.[record.status] || record.status}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '1rem', fontSize: '13px' }}>{new Date(record.created_at).toLocaleDateString()}</td>
                                                    <td className="action-buttons" style={{ padding: '1rem' }}>
                                                        <button onClick={() => viewShipmentDetails(record.id)} className="action-btn view" style={{ marginRight: '0.5rem', padding: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6' }}>
                                                            <FontAwesomeIcon icon={faEye} />
                                                        </button>
                                                        <button onClick={() => editShipment(record.id)} className="action-btn edit" style={{ marginRight: '0.5rem', padding: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#f59e0b' }}>
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                        <button onClick={() => deleteShipment(record.id)} className="action-btn delete" style={{ padding: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444' }}>
                                                            <FontAwesomeIcon icon={faTrash} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div className="pagination-modern" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem' }}>
                                        <button onClick={() => setHistoryPage(prev => Math.max(1, prev - 1))} disabled={historyPage === 1} className="pagination-btn" style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer' }}>
                                            {translations?.previous || 'Previous'}
                                        </button>
                                        <span className="pagination-info">{translations?.showing || 'Page'} {historyPage} {translations?.of || 'of'} {totalPages}</span>
                                        <button onClick={() => setHistoryPage(prev => Math.min(totalPages, prev + 1))} disabled={historyPage === totalPages} className="pagination-btn" style={{ padding: '0.5rem 1rem', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', cursor: 'pointer' }}>
                                            {translations?.next || 'Next'}
                                        </button>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContainerPriceCalculation;